
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Lottie from 'lottie-react';
import moment from 'moment';
import { IoIosArrowForward, IoIosArrowBack } from 'react-icons/io';

import { GET_AVAILABLE_SLOTS } from '../../../apollo/queries';
import { SCHEDULE_CAL_APPOINTMENT } from '../../../apollo/mutations';
import { useGetParams } from 'components/evieOnboarding/utils/useGetParams';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { KEYS } from 'components/evieOnboarding/utils/constants';
import heartLoader from 'assets/heart-loader.json';

import './appointment-scheduler-modal.scss';

const AppointmentSchedulerModal = ({ isOpen, onClose, refetch, eventId, eventKey }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const { t } = useTranslation();
  const {
    i18n: { language },
  } = useTranslation();

  const browserTimezone = String(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const { urlToken } = useGetParams();
  const [userToken] = useLocalStorage(KEYS.USER_TOKEN);
  const token = urlToken || userToken;

  const getDateRange = () => {
    const now = moment();
    const startDate = now.clone().add('hours').startOf('minute').utc();
    const endDate = now.clone().add(7, 'days').endOf('day').utc();

    return {
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString()
    };
  };

  const { startTime, endTime } = getDateRange();

  const { loading: slotsLoading, data: availableSlotsData = {} } = useQuery(GET_AVAILABLE_SLOTS, {
    skip: !urlToken && !userToken,
    variables: {
      eventTypeId: eventId,
      startTime: startTime,
      endTime: endTime,
      timezone: browserTimezone
    },
    context: {
      headers: {
        language: language.slice(0, 2),
        authorization: token ? `Bearer ${token}` : '',
        role: 'individual',
      },
    },
  });

  const [ScheduleCalAppointment, { loading: schedulingLoading }] = useMutation(SCHEDULE_CAL_APPOINTMENT, {
    context: {
      headers: {
        language: language.slice(0, 2),
        authorization: token ? `Bearer ${token}` : '',
        role: 'individual',
      },
    },
  });

  const availableSlots = availableSlotsData?.calAvailableTimes?.availableSlots;

  const getCalendarTitle = () => {
    if (!availableSlots?.length) {
      return moment().locale(language).format('MMM YYYY');
    }

    const months = availableSlots
      .slice(currentDayIndex, currentDayIndex + 3)
      .map(([dateString]) => moment(dateString).locale(language).format('MMM YYYY'));

    const uniqueMonths = [...new Set(months)];

    return uniqueMonths.length === 1
      ? uniqueMonths[0]
      : uniqueMonths.join(' - ');
  };

  const handleViewMore = () => {
    setIsExpanded(!isExpanded);
  };

  const handleNavigateDays = (direction) => {
    const step = 3;

    if (direction === 'next' && availableSlots && currentDayIndex + step < availableSlots.length) {
      setCurrentDayIndex(currentDayIndex + step);
    } else if (direction === 'prev' && currentDayIndex >= step) {
      setCurrentDayIndex(currentDayIndex - step);
    }
  };

  const handleTimeSelection = (day, hour) => {
    setSelectedDateTime({
      day: {
        dateString: day.dateString,
        dayShortName: day.dayShortName,
        dayNumber: day.dayNumber,
        index: day.index
      },
      hour: {
        timeString: hour.timeString,
        displayTime: hour.displayTime,
        index: hour.timeIndex,
      }
    });
  };

  const navigateToPage = (page) => {
    if (page === 2 && !selectedDateTime) return;
    setCurrentPage(page);
  };

  const handleConfirmAppointment = async () => {
    try {
      const formattedStartTime = moment(selectedDateTime.hour.timeString).utc().toISOString();
      const baseNotes = `Appointment scheduled for ${selectedDateTime.day.dayShortName} ${selectedDateTime.day.dayNumber} at ${selectedDateTime.hour.displayTime}`;
      const finalNotes = appointmentNotes ? appointmentNotes : baseNotes;

      const response = await ScheduleCalAppointment({
        variables: {
          eventKey: eventKey,
          requestedStartTime: formattedStartTime,
          timezone: browserTimezone,
          notes: finalNotes
        }
      });

      if (response.data?.scheduleCalAppointment?.errors && response.data.scheduleCalAppointment.errors.length > 0) {
        console.error('Errors scheduling appointment:', response.data.scheduleCalAppointment.errors);
        return;
      }
      setCurrentPage(3);
    } catch (error) {
      console.error('Error scheduling appointment:', error);
    }
  };

  const handleCloseModal = (event) => {
    event.stopPropagation()
    if (refetch && currentPage === 3) {
      refetch();
      setTimeout(() => {
        onClose();
      }, 500);
      return;
    }
    setCurrentPage(1);
    setSelectedDateTime(null);
    setIsExpanded(false);
    setCurrentDayIndex(0);
    setAppointmentNotes('');
    onClose();

  };

  const renderSelectionPage = () => (
    <>
      <div className="schedule-call-card__header">
        <button
          className="schedule-call-card__back-button"
          onClick={handleCloseModal}
          type="button"
        >
          ← {t('evieApp.scheduleCall.modal.back')}
        </button>
        <p className="schedule-call-card__header-title">{t('evieApp.scheduleCall.modal.title')}</p>
        <div className="schedule-call-card__session-info">
          <div className="schedule-call-card__session-item">
            <span className="schedule-call-card__session-icon">📹</span>
            <span>{t('evieApp.scheduleCall.modal.videoCall')}</span>
          </div>
          <div className="schedule-call-card__session-item">
            <span className="schedule-call-card__session-icon">⏰</span>
            <span>{t('evieApp.scheduleCall.modal.duration')}</span>
          </div>
        </div>
        <p className="schedule-call-card__timezone-text">{t('evieApp.scheduleCall.modal.timezone')}</p>
        <span className="schedule-call-card__timezone">{browserTimezone}</span>
      </div>

      <div className="schedule-call-card__calendar-content">
        <div className='schedule-call-card__calendar-date'>
          <button
            className='schedule-call-card__calendar-nav-button'
            onClick={() => handleNavigateDays('prev')}
            disabled={currentDayIndex === 0}
            type="button"
          >
            <IoIosArrowBack size={20} />
          </button>
          {(() => {
            const result = getCalendarTitle();
            return <p className='schedule-call-card__calendar-title'>{result}</p>
          })()}
          <button
            className='schedule-call-card__calendar-nav-button'
            onClick={() => handleNavigateDays('next')}
            disabled={!availableSlots || currentDayIndex + 3 >= availableSlots.length}
            type="button"
          >
            <IoIosArrowForward size={20} />
          </button>
        </div>
        {slotsLoading ? (
          <div className="schedule-call-card__calendar" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
          }}>
            <Lottie animationData={heartLoader} loop style={{ width: 100, height: 80 }} />
          </div>
        ) : (
          <div className="schedule-call-card__calendar">
            {availableSlots?.slice(currentDayIndex, currentDayIndex + 3).map((dayArray, index) => {
              const dateString = dayArray[0];
              const timeSlots = dayArray[1];
              const dayShortName = moment(dateString).locale(language).format('ddd');
              const dayNumber = moment(dateString).date();

              return (
                <div key={`day-${index}`} className="schedule-call-card__calendar-day">
                  <div className="schedule-call-card__calendar-day-header">
                    <span>{dayShortName}</span>
                    <span>{dayNumber}</span>
                  </div>
                  <div className={`schedule-call-card__calendar-day-hours ${isExpanded ? 'expanded' : ''}`}>
                    {
                      timeSlots?.map((timeSlot, timeIndex) => {
                        const timeString = timeSlot.start;
                        const displayTime = moment(timeString).format('h:mm A');

                        return (
                          <button
                            key={`time-${index}-${timeIndex}`}
                            className={`schedule-call-card__calendar-day-hours-button ${selectedDateTime?.day?.index === (currentDayIndex + index) && selectedDateTime?.hour?.index === timeIndex ? 'selected' : ''
                              }`}
                            onClick={() => handleTimeSelection({ dateString, dayShortName, dayNumber, index: currentDayIndex + index }, { timeString, displayTime, timeIndex })}
                            type="button"
                          >
                            {displayTime}
                          </button>
                        );
                      })
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="schedule-call-card__view-more">
          <button
            className={`schedule-call-card__view-more-button ${isExpanded ? 'expanded' : ''}`}
            onClick={handleViewMore}
            type="button"
          >
            {t('evieApp.scheduleCall.modal.viewMore')}
            <span className="schedule-call-card__view-more-icon">↓</span>
          </button>
        </div>

      </div>
      <div className="schedule-call-card__footer">
        <p className="schedule-call-card__selection-message">
          {selectedDateTime
            ? `${t('evieApp.scheduleCall.modal.selection.selected')} ${selectedDateTime.day.dayShortName} ${selectedDateTime.day.dayNumber} at ${selectedDateTime.hour.displayTime}`
            : t('evieApp.scheduleCall.modal.selection.message')
          }
        </p>
        <button
          className="schedule-call-card__continue-button"
          disabled={!selectedDateTime}
          onClick={() => navigateToPage(2)}
          type="button"
        >
          {t('evieApp.scheduleCall.modal.continue')}
        </button>
      </div>
    </>
  );

  const renderConfirmationPage = () => (
    <>
      {schedulingLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          zIndex: 10,
          borderRadius: '10px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Lottie animationData={heartLoader} loop style={{ width: 100, height: 80 }} />
          </div>
        </div>
      )}
      <div className="schedule-call-card__header-container">
        <div className="schedule-call-card__header">
          <button
            className="schedule-call-card__back-button"
            onClick={() => navigateToPage(1)}
            type="button"
          >
            ← {t('evieApp.scheduleCall.modal.back')}
          </button>
          <p className="schedule-call-card__header-title">{t('evieApp.scheduleCall.modal.confirm.title')}</p>
        </div>
        <div className="schedule-call-card__confirmation-section">
          <div className="schedule-call-card__appointment-details">
            <div className="schedule-call-card__detail-item">
              <span className="schedule-call-card__detail-label">{t('evieApp.scheduleCall.modal.confirm.duration')}</span>
              <span className="schedule-call-card__detail-value">{t('evieApp.scheduleCall.modal.duration')}</span>
            </div>
            <div className="schedule-call-card__detail-item">
              <span className="schedule-call-card__detail-label">{t('evieApp.scheduleCall.modal.confirm.date')}</span>
              <span className="schedule-call-card__detail-value">
                {selectedDateTime?.day.dayShortName} {selectedDateTime?.day.dayNumber}, {moment(selectedDateTime?.day.dateString).format('MMMM YYYY')}
              </span>
            </div>
            <div className="schedule-call-card__detail-item">
              <span className="schedule-call-card__detail-label">{t('evieApp.scheduleCall.modal.confirm.time')}</span>
              <span className="schedule-call-card__detail-value">{selectedDateTime?.hour.displayTime}</span>
            </div>
          </div>

          <div className="schedule-call-card__notes-section">
            <h3 className="schedule-call-card__notes-title">{t('evieApp.scheduleCall.modal.notes.title')}</h3>
            <textarea
              className="schedule-call-card__notes-textarea"
              rows="3"
              value={appointmentNotes}
              onChange={(e) => setAppointmentNotes(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="schedule-call-card__confirmation-footer">
        <p className="schedule-call-card__confirmation-message">
          {t('evieApp.scheduleCall.modal.confirm.message')}
        </p>
        <button
          className="schedule-call-card__confirm-button"
          onClick={handleConfirmAppointment}
          disabled={schedulingLoading}
          type="button"
        >
          {t('evieApp.scheduleCall.modal.confirm.button')}
        </button>
      </div>
    </>
  );

  const renderSuccessPage = () => (
    <>
      <div className="schedule-call-card__success-section">
        <div className="schedule-call-card__success-icon">🎉</div>
        <h2 className="schedule-call-card__success-title">{t('evieApp.scheduleCall.modal.success.title')}</h2>
        <p className="schedule-call-card__success-message">
          {t('evieApp.scheduleCall.modal.success.message')}{' '}
          <strong>{moment(selectedDateTime?.day.dateString).locale(language).format('dddd, MMMM D, YYYY')}</strong> {' - '}
          <strong>{selectedDateTime?.hour.displayTime}</strong>.
        </p>
        <div className="schedule-call-card__success-details">
          <p>{t('evieApp.scheduleCall.modal.success.details')}</p>
          <p>{t('evieApp.scheduleCall.modal.success.lookingForward')}</p>
        </div>
      </div>
      <div className="schedule-call-card__success-footer">
        <button
          className="schedule-call-card__done-button"
          onClick={handleCloseModal}
          type="button"
        >
          {t('evieApp.scheduleCall.modal.success.done')}
        </button>
      </div>
    </>
  );

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="schedule-call-card__overlay" onClick={onClose} >
      <div className="schedule-call-card__calendar-container" onClick={(event) => event.stopPropagation()}>
        {currentPage === 1 && renderSelectionPage()}
        {currentPage === 2 && renderConfirmationPage()}
        {currentPage === 3 && renderSuccessPage()}
      </div>
    </div>,
    document.body,
  );
};

AppointmentSchedulerModal.defaultProps = {
  eventId: 3073574,
  eventKey: 'discovery_call',
}

AppointmentSchedulerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refetch: PropTypes.func,
  availableHours: PropTypes.array,
  augustDays: PropTypes.array,
  eventId: PropTypes.number,
  eventKey: PropTypes.string,
};

export default AppointmentSchedulerModal; 
